// FrequentationEntries component
// UPDATED: Renamed title to "Détails", removed total counter, added sortable column headers,
//   renamed "Coach assigné" to "Utilisateur assigné", fixed mobile scroll clipping.
// UPDATED: Added Horaire column and CSV export button.
//   Rendering extracted to FrequentationEntriesTable.tsx.
// UPDATED: Default sort is now ascending by date (sortField initial = 'occurrenceDate').
// UPDATED: CSV export uses DD/MM/YYYY date format and HH:MM time format for Excel compatibility.
// FIXED: CSV export catch now logs error and shows inline exportError message instead of swallowing silently.

import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, List, Download } from 'lucide-react';
import {
  getFrequentationEntries,
  getFrequentationEntriesCount,
  type FrequentationEntriesFilters,
  type FrequentationEntry,
} from '../../lib/analytics';
import { createCSVBlob } from '../../lib/csvEncoding';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';
import {
  DesktopTable,
  MobileList,
  sortEntries,
  type SortField,
  type SortDirection,
} from './FrequentationEntriesTable';

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

interface FrequentationEntriesProps {
  filters: FrequentationEntriesFilters;
}

function formatDateCsv(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

function formatTimeCsv(time: string | null): string {
  if (!time) return '';
  return time.substring(0, 5);
}

function buildCSV(entries: FrequentationEntry[]): string {
  const headers = ['Date', 'Horaire', 'Mission', 'Utilisateur assigné', 'Fréquentation'];
  const rows = entries.map(e => [
    formatDateCsv(e.occurrenceDate),
    formatTimeCsv(e.scheduledTime),
    e.missionName,
    e.userName ?? '',
    e.numericValue != null ? String(e.numericValue) : ''
  ]);
  return [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
}

export function FrequentationEntries({ filters }: FrequentationEntriesProps) {
  const [entries, setEntries] = useState<FrequentationEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(25);
  const [sortField, setSortField] = useState<SortField | null>('occurrenceDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const filtersKey = `${filters.dateFrom}|${filters.dateTo}|${filters.siteId ?? ''}|${filters.templateId ?? ''}|${filters.scheduledTime ?? ''}`;

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const offset = (page - 1) * pageSize;
    Promise.all([
      getFrequentationEntries(filters, pageSize, offset),
      getFrequentationEntriesCount(filters),
    ])
      .then(([items, count]) => {
        if (!cancelled) {
          setEntries(items);
          setTotalCount(count);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erreur de chargement');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, page, pageSize]);

  const sortedEntries = useMemo(
    () => sortEntries(entries, sortField, sortDirection),
    [entries, sortField, sortDirection]
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }

  async function handleExportCsv() {
    setExporting(true);
    setExportError(null);
    try {
      const allEntries = await getFrequentationEntries(filters, 10000, 0);
      const sorted = sortEntries(allEntries, sortField, sortDirection);
      const csv = buildCSV(sorted);
      const blob = createCSVBlob(csv);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `frequentation_${filters.dateFrom}_${filters.dateTo}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[FrequentationEntries] CSV export failed:', err);
      setExportError("L'export a échoué. Veuillez réessayer.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <List size={15} className="text-gray-400 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-700">Détails</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting || loading || totalCount === 0}
            title="Exporter en CSV"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download size={13} className={exporting ? 'animate-pulse' : ''} />
            <span className="hidden sm:inline">{exporting ? 'Export...' : 'Exporter CSV'}</span>
          </button>
          <select
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value) as PageSize);
              setPage(1);
            }}
            className="text-xs text-gray-600 border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>
                {size} par page
              </option>
            ))}
          </select>
        </div>
      </div>
        {exportError && (
          <p className="text-xs text-red-600">{exportError}</p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <BouncingDotsLoader size="md" />
        </div>
      ) : error ? (
        <div className="p-6 text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-sm text-gray-400">Aucune séance sur cette période</p>
        </div>
      ) : (
        <>
          <DesktopTable
            entries={sortedEntries}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <MobileList entries={sortedEntries} />

          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {rangeStart}–{rangeEnd} sur {totalCount.toLocaleString('fr-FR')}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-xs text-gray-600 px-2 tabular-nums">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
